import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for barcode scanner integration
 * Detects barcode scanner input by monitoring rapid keypress sequences
 *
 * @param {Function} onScan - Callback function when barcode is detected
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether scanning is enabled (default: true)
 * @param {number} options.minLength - Minimum barcode length (default: 3)
 * @param {number} options.timeout - Max time between characters in ms (default: 50)
 * @param {string[]} options.endKeys - Keys that end barcode input (default: ['Enter'])
 * @param {string[]} options.startKeys - Optional prefix keys to detect scanner
 *
 * @example
 * useBarcodeScanner((barcode) => {
 *   console.log('Scanned:', barcode);
 *   searchProduct(barcode);
 * }, { enabled: true });
 */
export const useBarcodeScanner = (onScan, options = {}) => {
  const {
    enabled = true,
    minLength = 3,
    timeout = 50,
    endKeys = ['Enter'],
    startKeys = [],
    preventDefault = true
  } = options;

  const barcodeBuffer = useRef('');
  const lastKeyTime = useRef(0);
  const timeoutRef = useRef(null);

  const resetBuffer = useCallback(() => {
    barcodeBuffer.current = '';
    lastKeyTime.current = 0;
  }, []);

  const handleKeyPress = useCallback((event) => {
    if (!enabled) return;

    const currentTime = Date.now();
    const timeSinceLastKey = currentTime - lastKeyTime.current;

    // If too much time has passed, reset buffer (user typing vs scanner)
    if (timeSinceLastKey > timeout && barcodeBuffer.current.length > 0) {
      resetBuffer();
    }

    lastKeyTime.current = currentTime;

    // Check if this is an end key (like Enter)
    if (endKeys.includes(event.key)) {
      if (barcodeBuffer.current.length >= minLength) {
        if (preventDefault) {
          event.preventDefault();
        }
        onScan(barcodeBuffer.current.trim());
        resetBuffer();
      }
      return;
    }

    // Ignore special keys except numbers, letters, and allowed characters
    if (event.key.length === 1) {
      barcodeBuffer.current += event.key;

      // Clear timeout and set new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Auto-reset after timeout period
      timeoutRef.current = setTimeout(() => {
        resetBuffer();
      }, timeout * 10);
    }
  }, [enabled, minLength, timeout, endKeys, onScan, resetBuffer, preventDefault]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyPress);
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [enabled, handleKeyPress]);

  return { resetBuffer };
};

/**
 * Alternative hook for manual barcode input handling
 * Useful for specific input fields
 *
 * @param {Function} onScan - Callback when barcode is detected
 * @param {Object} options - Configuration options
 */
export const useBarcodeInput = (onScan, options = {}) => {
  const {
    minLength = 3,
    maxLength = 50,
    autoSubmit = true,
    submitDelay = 100
  } = options;

  const timeoutRef = useRef(null);

  const handleInput = useCallback((value) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Validate length
    if (value.length < minLength || value.length > maxLength) {
      return;
    }

    // Auto-submit after delay (simulates barcode scanner behavior)
    if (autoSubmit) {
      timeoutRef.current = setTimeout(() => {
        onScan(value.trim());
      }, submitDelay);
    } else {
      onScan(value.trim());
    }
  }, [minLength, maxLength, autoSubmit, submitDelay, onScan]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { handleInput };
};

export default useBarcodeScanner;
